import { useState, useEffect } from 'react';

export const useResponsive = () => {
  // Base layout dimensions - matched to 15.6" display
  const baseWidth = 1366;
  const baseHeight = 800;
  const maxScaleFactor = 1.25; // Maximum scale factor for large screens

  // Responsive states
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  
  // Scaling states
  const [shouldScale, setShouldScale] = useState(false);
  const [scale, setScale] = useState(1);
  const [marginAdjustment, setMarginAdjustment] = useState(0);
  
  // Screen type for specific styling
  const [screenType, setScreenType] = useState('desktop');

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      
      // Set device type flags
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
      setIsDesktop(width >= 1024);
      
      // Close sidebar on mobile when resizing
      if (width >= 1024) {
        // This could be used to close mobile sidebars
      }
      
      // Determine specific screen type
      if (width < 640) {
        setScreenType('mobile');
      } else if (width >= 640 && width < 1024) {
        setScreenType('tablet');
      } else if (width >= 1024 && width < 1600) {
        setScreenType('desktop');
      } else if (width >= 1600 && width <= 2560) {
        setScreenType('large-desktop');
      } else {
        setScreenType('ultra-wide');
      }
      
      // Handle scaling logic
      if (width < baseWidth) {
        // For very small screens, use mobile layout without scaling
        if (width < 640) {
          setShouldScale(false);
          setScale(1);
          setMarginAdjustment(0);
        } else {
          // For medium-small screens (tablets), use controlled scaling
          setShouldScale(true);
          const newScale = width / baseWidth;
          setScale(newScale);
          setMarginAdjustment(`${baseHeight - (baseHeight * newScale)}px`);
        }
      } 
      // Handle large screens - controlled upscaling
      else if (width > 1800) {
        setShouldScale(true);
        // Calculate upscale factor with a maximum limit
        const upscaleFactor = Math.min(maxScaleFactor, width / baseWidth);
        setScale(upscaleFactor);
        // No margin adjustment needed for upscaling
        setMarginAdjustment(0);
      } 
      // Handle medium screens - no scaling
      else {
        setShouldScale(false);
        setScale(1);
        setMarginAdjustment(0);
      }
    };
    
    // Calculate on mount
    handleResize();
    
    // Add resize listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to get container classes based on screen type
  const getContainerClasses = () => {
    switch (screenType) {
      case 'mobile':
        return 'px-4 py-4';
      case 'tablet':
        return 'px-4 py-4';
      case 'desktop':
        return 'px-6 py-6';
      case 'large-desktop':
        return 'px-8 py-8';
      case 'ultra-wide':
        return 'px-12 py-8';
      default:
        return 'px-6 py-6';
    }
  };

  // Helper function to get grid classes for card layouts
  const getGridClasses = () => {
    if (isMobile) {
      return "grid grid-cols-1 gap-4";
    } else if (isTablet) {
      return "grid grid-cols-2 gap-4";
    } else {
      return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 gap-y-8";
    }
  };

  // Helper function to get stats card grid classes
  const getStatsCardClasses = () => {
    if (isMobile) {
      return "grid grid-cols-2 gap-2";
    } else {
      return "grid grid-cols-2 md:grid-cols-4 gap-4";
    }
  };

  // Helper function to get scaling container style
  const getScalingContainerStyle = () => {
    if (shouldScale) {
      return {
        width: `${baseWidth}px`,
        height: `${baseHeight}px`,
        transform: `scale(${scale})`,
        transformOrigin: 'top center',
        marginBottom: marginAdjustment,
        marginLeft: 'auto',
        marginRight: 'auto'
      };
    }
    return {};
  };

  // Helper function to get main content style (for desktop layouts) - GROWING APPROACH
 // Helper function to get main content style (for desktop layouts) - PROPER GROWING APPROACH
const getMainContentStyle = () => {
  if (shouldScale) {
    const currentWidth = window.innerWidth;
    
    if (currentWidth >= 3400) {
      // Ultra-wide (3440x1440): MUCH BIGGER to fill ultrawide screen
      return {
        width: 'calc(95% + 12cm)',  // Much bigger for ultrawide
        height: 'calc(90vh - 0.5cm)',
        marginTop: 'calc(0.5cm + 0.35cm)',
        marginLeft: 'calc(13.5% + 0.5rem)',
        display: 'flex',
        flexDirection: 'column'
      };
    } else if (currentWidth >= 2500) {
      // Large (2560x1440): Bigger to fill more space  
      return {
        width: 'calc(92% + 9cm)',  // Bigger than current
        height: 'calc(90vh - 0.5cm)',
        marginTop: 'calc(0.5cm + 0.35cm)',
        marginLeft: 'calc(13.5% + 0.5rem)',
        display: 'flex',
        flexDirection: 'column'
      };
    } else {
      // Standard (1920x1080): Keep original working size
      return {
        width: 'calc(85% + 6.9cm)',
        height: 'calc(90vh - 0.5cm)',
        marginTop: 'calc(0.5cm + 0.35cm)',
        marginLeft: '0',
        display: 'flex',
        flexDirection: 'column'
      };
    }
  } else {
    return {
      width: 'calc(85% + 6.9cm)',
      height: 'calc(90vh - 0.5cm)',
      marginTop: 'calc(0.5cm + 0.35cm)',
      marginLeft: 'calc(1.1cm)',
      display: 'flex',
      flexDirection: 'column'
    };
  }
};

  return {
    // Device type flags
    isMobile,
    isTablet,
    isDesktop,
    
    // Scaling properties
    shouldScale,
    scale,
    marginAdjustment,
    screenType,
    
    // Base dimensions
    baseWidth,
    baseHeight,
    maxScaleFactor,
    
    // Helper functions
    getContainerClasses,
    getGridClasses,
    getStatsCardClasses,
    getScalingContainerStyle,
    getMainContentStyle
  };
};